// @flow

/*
 ** Imports
 */

// Modules
import React, { memo } from 'react';
import styled from 'styled-components';
import { range } from 'lodash';

// Styles
import { PRIMARY } from 'src/styles/palettes';

/*
 ** Styled
 */

const RootDot = styled.div`
  display: flex;
  padding: 0.5em;
  justify-content: center;
  align-items: center;
`;

const Dot = styled.div`
  width: ${(props) => (props.size ? `${props.size}em` : '0.5em')};
  height: ${(props) => (props.size ? `${props.size}em` : '0.5em')};
  border-radius: 50%;
  background-color: ${(props) => props.dotColor || PRIMARY};
  animation: loader 1.2s ease infinite;
  opacity: 0;
  transform: scale(0);

  :nth-child(1) {
    margin-right: ${(props) => (props.size ? `${props.size}em` : '0.5em')};
  }

  :nth-child(2) {
    margin-right: ${(props) => (props.size ? `${props.size}em` : '0.5em')};
    animation-delay: 0.2s;
  }

  :nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes loader {
    0%,
    10%,
    90%,
    100% {
      opacity: 0;
      transform: scale(0);
    }

    50% {
      opacity: 0.5;
      transform: scale(1);
    }
  }
`;

const RootSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContainerSpinner = styled.div`
  position: relative;
  width: ${(props) => (props.size && props.size / 0.15) || 1}em;
  height: ${(props) => (props.size && props.size / 0.15) || 1}em;
`;

const Spinner = styled.div`
  position: absolute;
  width: ${(props) => (props.size && `${props.size}em`) || '0.15em'};
  height: ${(props) => (props.size && `${props.size}em`) || '0.15em'};
  border-radius: 50%;
  background-color: ${(props) => props.dotColor || PRIMARY};
  animation: spinner 0.8s linear infinite;
  opacity: 0.1;
  transform: scale(0.5);

  :nth-child(1) {
    top: 0em;
    left: ${(props) => (props.size && `${props.size * (0.425 / 0.15)}em`) || '0.425em'};
  }

  :nth-child(2) {
    top: ${(props) => (props.size && `${props.size * (0.1245 / 0.15)}em`) || '0.1245em'};
    left: ${(props) => (props.size && `${props.size * (0.7255 / 0.15)}em`) || '0.7255em'};
    animation-delay: 0.1s;
  }

  :nth-child(3) {
    top: ${(props) => (props.size && `${props.size * (0.425 / 0.15)}em`) || '0.425em'};
    left: ${(props) => (props.size && `${props.size * (0.85 / 0.15)}em`) || '0.85em'};
    animation-delay: 0.2s;
  }

  :nth-child(4) {
    top: ${(props) => (props.size && `${props.size * (0.7255 / 0.15)}em`) || '0.7255em'};
    left: ${(props) => (props.size && `${props.size * (0.7255 / 0.15)}em`) || '0.7255em'};
    animation-delay: 0.3s;
  }

  :nth-child(5) {
    top: ${(props) => (props.size && `${props.size * (0.85 / 0.15)}em`) || '0.85em'};
    left: ${(props) => (props.size && `${props.size * (0.425 / 0.15)}em`) || '0.425em'};
    animation-delay: 0.4s;
  }

  :nth-child(6) {
    top: ${(props) => (props.size && `${props.size * (0.7255 / 0.15)}em`) || '0.7255em'};
    left: ${(props) => (props.size && `${props.size * (0.1245 / 0.15)}em`) || '0.1245em'};
    animation-delay: 0.5s;
  }

  :nth-child(7) {
    top: ${(props) => (props.size && `${props.size * (0.425 / 0.15)}em`) || '0.425em'};
    left: 0em;
    animation-delay: 0.6s;
  }

  :nth-child(8) {
    top: ${(props) => (props.size && `${props.size * (0.1245 / 0.15)}em`) || '0.1245em'};
    left: ${(props) => (props.size && `${props.size * (0.1245 / 0.15)}em`) || '0.1245em'};
    animation-delay: 0.7s;
  }

  @keyframes spinner {
    0%,
    10%,
    90%,
    100% {
      opacity: 0.1;
      transform: scale(0.5);
    }

    50% {
      opacity: 0.8;
      transform: scale(1);
    }
  }
`;

/*
 ** Types
 */

type PropTypes = {
  className?: string,
  type?: string,
  dotColor?: string,
  size?: number
};

const LOADER_DOT_COUNT = 3;
const SPINNER_DOT_COUNT = 8;

/**
 * Component
 */

const Loader = ({ className, type, dotColor, size }: PropTypes) => {
  if (type === 'spinner') {
    return (
      <RootSpinner className={className}>
        <ContainerSpinner isSpinner size={size}>
          {range(SPINNER_DOT_COUNT).map((index) => (
            <Spinner key={index} size={size} dotColor={dotColor} />
          ))}
        </ContainerSpinner>
      </RootSpinner>
    );
  } else {
    return (
      <RootDot className={className}>
        {range(LOADER_DOT_COUNT).map((index) => (
          <Dot key={index} size={size} dotColor={dotColor} />
        ))}
      </RootDot>
    );
  }
};

export default (memo<PropTypes>(Loader): React$AbstractComponent<PropTypes, mixed>);
